import { useEffect, useRef } from 'react';
import Mousetrap from 'mousetrap';

type EscapeHandler = (event: KeyboardEvent) => void;

const activeHandlerRefs: { current?: EscapeHandler }[] = [];

const runNewestHandler = (event: KeyboardEvent) => {
  activeHandlerRefs[activeHandlerRefs.length - 1]?.current?.(event);
};

/**
 * Calls `onEscape` when Esc is pressed while `isActive` is true. When several components use this
 * at once, only the most recently activated one runs, so Esc closes the top modal and leaves the
 * one under it open. An active caller without `onEscape` still takes Esc and does nothing.
 *
 * Always calls the latest `onEscape`, so it can change between renders (e.g. to block closing
 * while an upload is running).
 *
 * Use this instead of binding `esc` with Mousetrap directly. Mousetrap keeps one handler per key,
 * so a direct bind replaces everyone else's and a direct unbind removes it for everyone.
 */
const useEscapeKey = (isActive: boolean, onEscape?: EscapeHandler) => {
  const handlerRef = useRef(onEscape);

  useEffect(() => {
    handlerRef.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    if (!isActive) return;

    activeHandlerRefs.push(handlerRef);
    Mousetrap.bind('esc', runNewestHandler);

    return () => {
      const index = activeHandlerRefs.indexOf(handlerRef);
      if (index !== -1) activeHandlerRefs.splice(index, 1);

      if (!activeHandlerRefs.length) Mousetrap.unbind('esc');
    };
  }, [isActive]);
};

export default useEscapeKey;
